import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { Download, X } from "lucide-react";

// CS 22.6 — HO-only export of ticked catalog designs (read-only; nothing is saved).
async function downloadExport(body) {
  const res = await api.post("/master-catalog/catalog/export.xlsx", body, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const name = (cd.match(/filename="([^"]+)"/) || [])[1] || "master-catalog-selection.xlsx";
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.click();
  URL.revokeObjectURL(href);
}

export const SelectionBar = ({ keys, variant, onClear }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({ include_franchise: true, include_mrp: true });
  const [busy, setBusy] = useState(false);
  if (!keys.length) return null;
  const go = async () => {
    setBusy(true);
    try {
      await downloadExport({ keys, color: variant.color, purity: variant.purity, ...opts });
      setOpen(false);
    } catch {
      toast.error("Export failed");
    } finally { setBusy(false); }
  };
  return (
    <>
      <div className="sticky bottom-24 md:bottom-4 z-30 mx-auto w-fit max-w-full flex items-center gap-2 sm:gap-3 rounded-full bg-[#0D1B2A] text-white pl-4 sm:pl-5 pr-2 py-2 shadow-[0_16px_40px_-16px_rgba(13,27,42,0.6)]" data-testid="mc-selection-bar">
        <span className="text-sm mc-num whitespace-nowrap" data-testid="mc-selection-count">{keys.length}<span className="hidden sm:inline"> design{keys.length === 1 ? "" : "s"}</span> selected</span>
        <button onClick={onClear} className="flex items-center gap-1 text-xs text-white/70 hover:text-white px-1 sm:px-2" data-testid="mc-selection-clear"><X className="w-3.5 h-3.5" />Clear</button>
        <Button onClick={() => { setOpts({ include_franchise: true, include_mrp: true }); setOpen(true); }} className="rounded-full bg-[#C9A84C] hover:bg-[#b8973f] text-[#0D1B2A] px-3 sm:px-4" data-testid="mc-selection-download">
          <Download className="w-4 h-4 mr-1.5" />Download<span className="hidden sm:inline">&nbsp;Excel</span>
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid="mc-export-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Download Excel</DialogTitle>
            <DialogDescription>{keys.length} design(s) in the current colour/purity view.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center justify-between text-sm">Include Franchise cost
              <Switch checked={opts.include_franchise} onCheckedChange={(v) => setOpts({ ...opts, include_franchise: v })} data-testid="mc-export-franchise-toggle" />
            </label>
            <label className="flex items-center justify-between text-sm">Include MRP
              <Switch checked={opts.include_mrp} onCheckedChange={(v) => setOpts({ ...opts, include_mrp: v })} data-testid="mc-export-mrp-toggle" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="mc-export-cancel">Cancel</Button>
            <Button onClick={go} disabled={busy} className="bg-[#0D1B2A] text-white" data-testid="mc-export-submit">{busy ? "Preparing…" : "Download"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
